import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Chip,
  Tabs,
  Tab
} from "@mui/material";
import { DataGrid, GridColDef, GridRowsProp } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import {
  PaginatedRequest,
  SortCriteriaDirectionEnum,
  CarDtoStatusEnum
} from "../../apiClient";
import { CarService } from "../../api/services/car.service";
import { UUID } from "crypto";
import { useKeycloak } from "@react-keycloak/web";
import { all_routes } from "../router/all_routes";
import InquiryBoard from "./inquiryBoard";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const Admin = () => {
  const navigate = useNavigate();
  const { keycloak } = useKeycloak();
  const routes = all_routes;
  const userRoles = keycloak.tokenParsed?.realm_access?.roles || [];
  const isAdmin = userRoles.includes('ADMIN');

  const [tabValue, setTabValue] = useState(0);

  const [paginateRequest, setPaginateRequest] =
    React.useState<PaginatedRequest>({
      page: 0,
      pageSize: 10,
      filters: [],
      sorters: [{ key: "make", direction: SortCriteriaDirectionEnum.Asc }],
    });

  const handleDelete = async (event: React.MouseEvent, id: UUID) => {
    event.stopPropagation();
    if(window.confirm("Sigur doriți să ștergeți această mașină?")) {
        try {
            await CarService.delete(id.toString());
            // Force refresh by creating a new object reference
            setPaginateRequest(prev => ({ ...prev }));
        } catch (error) {
            console.error("Failed to delete car", error);
            alert("Nu s-a putut șterge mașina.");
        }
    }
  };

  const columns: GridColDef[] = [
    { 
        field: "image", 
        headerName: "Imagine", 
        width: 100,
        renderCell: (params) => {
            const getImageUrl = () => {
                const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
                
                // 1. Try to find a primary image or first image from the list
                    if (params.row.images && params.row.images.length > 0) {
                    const primary = params.row.images.find((img: any) => img.isPrimary);
                    const file = primary || params.row.images[0];
                    if (file && file.id) {
                        return `${apiUrl}/api/v1/public/car/image/${file.id}`;
                    }
                }

                // 2. Fallback to legacy string field
                const img = params.value as string;
                if (!img) return "assets/img/cars/car-01.jpg";
                
                if (img.startsWith('http')) return img;
                
                // If it looks like a ID
                if (img.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
                    return `${apiUrl}/api/v1/public/car/image/${img}`;
                }
                
                // Otherwise relative path
                return `${apiUrl}${img.startsWith('/') ? '' : '/'}${img}`;
            };
            
            return (
                <img
                src={getImageUrl()}
                alt={params.row.model}
                width="60"
                height="40"
                style={{ borderRadius: "4px", objectFit: "cover" }}
                />
            );
        },
    },
    { field: "make", headerName: "Marcă", width: 120 },
    { field: "model", headerName: "Model", width: 120 },
    { field: "year", headerName: "An", width: 80, valueGetter: (value: any, row: any) => row?.features?.year },
    { field: "vin", headerName: "VIN", width: 150 },
    { field: "price", headerName: "Preț (€)", width: 100 },
    { field: "discountAmount", headerName: "Discount", width: 100 },
    { field: "finalPrice", headerName: "Final (€)", width: 100 },
    { 
        field: "status", 
        headerName: "Status", 
        width: 120,
        renderCell: (params) => (
            <Chip 
                label={params.value} 
                color={params.value === CarDtoStatusEnum.Available ? "success" : params.value === CarDtoStatusEnum.Sold ? "error" : "warning"}
                size="small"
            />
        )
    },
    {
      field: "actions",
      headerName: "Acțiuni",
      width: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
            variant="outlined"
            size="small"
            onClick={() => navigate(`/admin/edit-car/${params.row.id}`)}
            >
            Editează
            </Button>
            <Button
            variant="outlined"
            size="small"
            color="error"
            onClick={(e) => handleDelete(e, params.row.id)}
            >
            Șterge
            </Button>
        </Box>
      ),
    },
  ];

  const [rows, setRows] = React.useState<GridRowsProp>([]);
  const [loading, setLoading] = React.useState<boolean>(false);

  useEffect(() => {
    async function fetchData() {
      if(!isAdmin) {
        navigate(all_routes.home);
      }
      setLoading(true);

      setLoading(true);

      const response = (await CarService.getPaginated(paginateRequest.page, paginateRequest.pageSize, paginateRequest)).data;

      if (response) {
        setRows(response);
        setLoading(false);
      }
    }
    fetchData();
  }, [paginateRequest, tabValue]); // Refresh when tab changes back to cars

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <Box sx={{ padding: 4 }}>
      <Grid
        container
        justifyContent="space-between"
        alignItems="center"
        sx={{ marginBottom: 2 }}
      >
        <Grid item>
          <Typography variant="h4">Panou Administrare</Typography>
        </Grid>
        <Grid item>
            {tabValue === 0 && (
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => navigate("/admin/add-car")}
                >
                    Adaugă mașină nouă
                </Button>
            )}
        </Grid>
      </Grid>
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="admin tabs">
          <Tab label="Inventar Auto" />
          <Tab label="Solicitări Clienți" />
        </Tabs>
      </Box>

      <CustomTabPanel value={tabValue} index={0}>
        <Card>
            <CardContent>
            <DataGrid
                rows={rows}
                columns={columns}
                pageSizeOptions={[10, 35, 70]}
                sortingMode="server"
                filterMode="server"
                paginationMode="server"
                paginationModel={{
                page: paginateRequest.page ?? 0,
                pageSize: paginateRequest.pageSize ?? 10,
                }}
                sortModel={(paginateRequest.sorters ?? []).map((sorter) => ({
                field: sorter.key ?? '',
                sort: (sorter?.direction?.toLowerCase() as 'asc' | 'desc') ?? 'asc',
                }))}
                onPaginationModelChange={(newModel) =>
                setPaginateRequest({
                    ...paginateRequest,
                    page: newModel.page,
                    pageSize: newModel.pageSize,
                })
                }
                onSortModelChange={(newModel) =>
                setPaginateRequest({
                    ...paginateRequest,
                    sorters: newModel.map((sort) => ({
                    key: sort?.field ?? '',
                    direction: (sort?.sort?.toUpperCase() as SortCriteriaDirectionEnum) ?? SortCriteriaDirectionEnum.Asc,
                    })),
                })
                }
                loading={loading}
                rowCount={rows.length}
                // onCellClick={(params) => navigate(`/admin/${params.id}`)} // Conflicts with button click
            />
            </CardContent>
        </Card>
      </CustomTabPanel>
      <CustomTabPanel value={tabValue} index={1}>
          <InquiryBoard />
      </CustomTabPanel>
    </Box>
  );
};

export default Admin;
